import { CalculationState } from '@/lib/calculationstate';
import params from '@/lib/calculationparams';
import { round } from 'lodash-es';

/**
 * Gets the yearly salary capped against the some (by default the UWV)
 * maximum insured salary.
 *
 * @param state
 * @param [maximum] Maximum salary. Defaults to the UWV Maximum-SV Salary
 * @return capped salary
 */
export function cappedSalary(state: CalculationState, maximum?: number): number {
  return Math.min(state.salary, maximum ?? params.uwv.maximumSalary);
}

/**
 * Calculates the correction factor (also known as F-Factor) for the current state.
 * The factor is a ratio of the UWV maximum salary compared with the previous yearly salary
 *
 * @param state
 * @param maximum Maximum salary. Defaults to the UWV Maximum-SV salary.
 * @return F-factor
 */
export function correctionFactor(state: CalculationState, maximum?: number): number {
  if (state.salary === 0) return 1;

  const maxSalary = maximum ?? params.uwv.maximumSalary;
  return Math.min(maxSalary / state.salary, 1);
}

/**
 * Returns the amount of money the disability ratio would still allow you to earn.
 * (Rest Verdien Capaciteit).
 *
 * @param state
 * @return remaining earnings capacity
 */
export function remainingEarningsCapacity(state: CalculationState): number {
  return round((1 - state.disabilityRatio) * state.salary, 2);
}

/**
 * Returns the ratio between the current (while labor-incapable) income and the
 * income possible per the disability ratio.
 *
 * @param state
 * @return {number}
 */
export function currentToRemainingIncomeRatio(state: CalculationState): number {
  return state.extraIncome / Math.max(remainingEarningsCapacity(state), 1);
}

/**
 * Calculates the loss of income against the previous income before disability.
 *
 * @param state
 */
export function incomeLossRatio(state: CalculationState): number {
  return 1 - state.extraIncome / state.salary;
}

/**
 * Performs a VLOOKUP-alike functionality, lookup up a tabular result given a number.
 *
 * It will attempt to find the last row that satisfies the condition table[0] < number,
 * returning table[1] for that row.
 * If the number is smaller than all rows, this function returns null.
 *
 * @param table Table to use for lookups, This function assumes the table is sorted by table[0]!
 * @param value Value to use for lookup
 *
 * @return lookup result
 */
export function lookupTable(table: [number, number][], value: number): number | null {
  // if lower than the lowest value in the table, return null
  if (value < table[0][0]) {
    return null;
  }

  // Find number that is smaller than value
  let i = 0;
  while (table[i + 1] && table[i + 1][0] <= value) {
    i += 1;
  }

  // return value
  return table[i][1];
}
